import { z } from "zod";
import { idString, IsoDate, LongText, MediumText, ShortText, } from "./common.js";
export const CONTAINMENT_PRIORITIES = ["High", "Medium", "Low"];
export const CONTAINMENT_ACTION_STATUSES = [
    "Open",
    "In progress",
    "Done",
    "Stopped",
];
export const CONTAINMENT_EFFECTIVENESS = [
    "Not yet assessed",
    "Effective",
    "Partially effective",
    "Not effective",
];
export const MAX_CONTAINMENT_ACTIONS = 100;
export const MAX_CONTAINMENT_NOTES_PER_ACTION = 500;
export const ContainmentPrioritySchema = z.enum(CONTAINMENT_PRIORITIES);
export const ContainmentActionStatusSchema = z.enum(CONTAINMENT_ACTION_STATUSES);
export const ContainmentEffectivenessSchema = z.enum(CONTAINMENT_EFFECTIVENESS);
const KnownOrUnknownDateSchema = z.union([IsoDate, z.literal("")]);
export const ContainmentNoteSchema = z
    .object({
    id: idString,
    author: ShortText.optional(),
    at: z.number(),
    text: LongText,
})
    .strict();
export const ContainmentPlanActionSchema = z
    .object({
    id: idString,
    action: ShortText,
    owner: ShortText,
    /** The tenant member `owner` names, when picked from the people picker. */
    ownerUserId: z.string().uuid().nullable().optional(),
    eta: KnownOrUnknownDateSchema,
    priority: ContainmentPrioritySchema,
    status: ContainmentActionStatusSchema,
    effectiveness: ContainmentEffectivenessSchema,
    notes: z.array(ContainmentNoteSchema).max(MAX_CONTAINMENT_NOTES_PER_ACTION),
    protectedScope: MediumText.optional(),
    startedOn: KnownOrUnknownDateSchema.optional(),
    verificationMethod: MediumText.optional(),
    verificationCadence: ShortText.optional(),
    successCriteria: MediumText.optional(),
    effectivenessEvidence: MediumText.optional(),
    sideEffectSafeguard: MediumText.optional(),
    escalationTrigger: MediumText.optional(),
    milestoneId: idString.optional(),
    jiraKey: ShortText.optional(),
})
    .strict();
export const ContainmentPlanParamsSchema = z
    .object({
    actions: z.array(ContainmentPlanActionSchema).max(MAX_CONTAINMENT_ACTIONS),
    revisitCadenceDays: z.number().int().min(1).optional(),
    nextReviewDate: KnownOrUnknownDateSchema.optional(),
})
    .strict();
export const defaultContainmentPlanParams = {
    actions: [],
};
export function isContainmentOverdue(action, now = new Date()) {
    if (action.status === "Done" || action.status === "Stopped")
        return false;
    if (!IsoDate.safeParse(action.eta).success)
        return false;
    return action.eta < toLocalIsoDate(now);
}
export function toLocalIsoDate(value) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}
export function containmentRollup(actions, now = new Date()) {
    const byStatus = {
        Open: 0,
        "In progress": 0,
        Done: 0,
        Stopped: 0,
    };
    const byEffectiveness = {
        "Not yet assessed": 0,
        Effective: 0,
        "Partially effective": 0,
        "Not effective": 0,
    };
    let overdue = 0;
    let unowned = 0;
    for (const action of actions) {
        byStatus[action.status] += 1;
        byEffectiveness[action.effectiveness] += 1;
        if (isContainmentOverdue(action, now))
            overdue += 1;
        if (action.owner.trim() === "")
            unowned += 1;
    }
    return {
        total: actions.length,
        byStatus,
        byEffectiveness,
        overdue,
        unowned,
    };
}
//# sourceMappingURL=containmentPlan.js.map